import { parsePhoneNumber, CountryCode } from 'libphonenumber-js';
import { phoneMessage } from '@/lib/phoneMessages';

function formatBrazilianWhatsAppNumber(input: string): string {
  const rawDigits = input.replace(/\D/g, '');
  const digits =
    rawDigits.length > 11 ? rawDigits.slice(-11) : rawDigits.slice(0, 11);

  if (digits.length === 0) return '';
  if (digits.length < 3) return `(${digits}`;
  if (digits.length < 8) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
}

function validateBrazilianWhatsAppRules(
  phoneNumber: string
): {
  isValid: boolean;
  formattedNumber: string;
  internationalNumber: string;
  errorMessage: string;
} {
  const trimmed = phoneNumber.trim();
  const rawDigits = trimmed.replace(/\D/g, '');
  const digits = rawDigits.length > 11 ? rawDigits.slice(-11) : rawDigits;
  const formatted = formatBrazilianWhatsAppNumber(digits);

  if (!trimmed) {
    return {
      isValid: false,
      formattedNumber: '',
      internationalNumber: '',
      errorMessage: phoneMessage('required')
    };
  }

  if (digits.length !== 11) {
    return {
      isValid: false,
      formattedNumber: formatted,
      internationalNumber: '',
      errorMessage: phoneMessage('brDigits')
    };
  }

  const ddd = Number(digits.slice(0, 2));
  const firstMobileDigit = digits[2];
  if (ddd < 11 || ddd > 99) {
    return {
      isValid: false,
      formattedNumber: formatted,
      internationalNumber: '',
      errorMessage: phoneMessage('brDdd')
    };
  }

  if (firstMobileDigit !== '9') {
    return {
      isValid: false,
      formattedNumber: formatted,
      internationalNumber: '',
      errorMessage: phoneMessage('brFirstDigit')
    };
  }

  return {
    isValid: true,
    formattedNumber: formatted,
    internationalNumber: `+55${digits}`,
    errorMessage: ''
  };
}

/**
 * Formata um número de telefone para o formato aceito pela API do WhatsApp
 * @param phoneNumber - Número de telefone de entrada
 * @param defaultCountry - País padrão (BR)
 * @returns Número formatado para WhatsApp (ex: "5511999999999") ou string vazia se inválido
 */
export function formatPhoneForWhatsApp(phoneNumber: string, defaultCountry: CountryCode = 'BR'): string {
  try {
    if (!phoneNumber || phoneNumber.trim() === '') {
      return '';
    }

    const trimmed = phoneNumber.trim();
    const explicitCountry = trimmed.startsWith('+')
      ? parsePhoneNumber(trimmed).country
      : undefined;

    if ((explicitCountry ?? defaultCountry) === 'BR') {
      const rawDigits = phoneNumber.replace(/\D/g, '');
      const digits = rawDigits.length > 11 ? rawDigits.slice(-11) : rawDigits;
      if (digits.length !== 11) return '';
      return `55${digits}`;
    }

    const parsed = parsePhoneNumber(phoneNumber, defaultCountry);
    
    if (!parsed || !parsed.isValid()) {
      return '';
    }

    // Retorna no formato que o WhatsApp aceita (apenas números, com código do país, sem +)
    // Exemplo: +5511999999999 -> 5511999999999
    return parsed.number.substring(1);
  } catch (error) {
    console.warn('Erro ao formatar telefone para WhatsApp:', error);
    return '';
  }
}

/**
 * Valida se dois números de telefone são equivalentes
 * @param phone1 - Primeiro número
 * @param phone2 - Segundo número 
 * @param defaultCountry - País padrão
 * @returns true se os números são equivalentes
 */
export function arePhoneNumbersEquivalent(
  phone1: string, 
  phone2: string, 
  defaultCountry: CountryCode = 'BR'
): boolean {
  try {
    if (!phone1 || !phone2) {
      return false;
    }

    const parsed1 = parsePhoneNumber(phone1, defaultCountry);
    const parsed2 = parsePhoneNumber(phone2, defaultCountry);
    
    if (!parsed1 || !parsed2 || !parsed1.isValid() || !parsed2.isValid()) {
      return false;
    }

    // Compara os números no formato internacional
    return parsed1.number === parsed2.number;
  } catch (error) {
    console.warn('Erro ao comparar números de telefone:', error);
    return false;
  }
}

/**
 * Valida se um número de telefone é válido para WhatsApp
 * @param phoneNumber - Número de telefone
 * @param defaultCountry - País padrão
 * @returns Objeto com resultado da validação
 */
export function validatePhoneForWhatsApp(
  phoneNumber: string, 
  defaultCountry: CountryCode = 'BR'
): {
  isValid: boolean;
  formattedNumber: string;
  internationalNumber: string;
  errorMessage: string;
} {
  try {
    const trimmed = phoneNumber.trim();
    const explicitCountry = trimmed.startsWith('+')
      ? parsePhoneNumber(trimmed).country
      : undefined;

    if ((explicitCountry ?? defaultCountry) === 'BR') {
      return validateBrazilianWhatsAppRules(phoneNumber);
    }

    if (!phoneNumber || phoneNumber.trim() === '') {
      return {
        isValid: false,
        formattedNumber: '',
        internationalNumber: '',
        errorMessage: phoneMessage('required')
      };
    }

    const parsed = parsePhoneNumber(phoneNumber, defaultCountry);
    
    if (!parsed) {
      return {
        isValid: false,
        formattedNumber: phoneNumber,
        internationalNumber: '',
        errorMessage: phoneMessage('invalidFormat')
      };
    }

    if (!parsed.isValid()) {
      return {
        isValid: false,
        formattedNumber: parsed.formatNational(),
        internationalNumber: parsed.formatInternational(),
        errorMessage: phoneMessage('invalid')
      };
    }

    // Verifica se é um número de celular (necessário para WhatsApp)
    const phoneType = parsed.getType();
    if (phoneType && !['MOBILE', 'FIXED_LINE_OR_MOBILE'].includes(phoneType)) {
      return {
        isValid: false,
        formattedNumber: parsed.formatNational(),
        internationalNumber: parsed.formatInternational(),
        errorMessage: phoneMessage('mobileRequired')
      };
    }

    return {
      isValid: true,
      formattedNumber: parsed.formatNational(),
      internationalNumber: parsed.formatInternational(),
      errorMessage: ''
    };
  } catch {
    return {
      isValid: false,
      formattedNumber: phoneNumber,
      internationalNumber: '',
      errorMessage: phoneMessage('validationError')
    };
  }
}
